/**
 * Client-side image compression utility.
 * Uses canvas-based resize + quality reduction before upload to save bandwidth.
 * Skips GIFs (animated) and already-small files (<50KB).
 */

const IMAGE_SIZE_THRESHOLD = 50 * 1024; // 50 KB — skip compression for small files
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;
const QUALITY = 0.75;

/**
 * Compress an image file by resizing and converting to WebP (or JPEG fallback).
 * Videos and non-image files are returned unchanged.
 *
 * @param {File} file - The file to compress
 * @returns {Promise<File>} - Compressed file (or original if compression is skipped/fails)
 */
export async function compressImageFile(file) {
  // Only compress images (skip videos, GIFs, SVGs)
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  if (file.size < IMAGE_SIZE_THRESHOLD) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    // Calculate scaled dimensions (only downscale, never upscale)
    let newWidth = width;
    let newHeight = height;
    if (width > MAX_WIDTH || height > MAX_HEIGHT) {
      const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
      newWidth = Math.round(width * ratio);
      newHeight = Math.round(height * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
    bitmap.close();

    // Try WebP first, fall back to JPEG
    const supportsWebP = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    const mimeType = supportsWebP ? 'image/webp' : 'image/jpeg';
    const ext = supportsWebP ? '.webp' : '.jpg';

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, QUALITY)
    );

    if (!blob || blob.size >= file.size) {
      // Compression didn't help — return original
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], baseName + ext, { type: mimeType, lastModified: Date.now() });
  } catch {
    // If anything fails, return original file unchanged
    return file;
  }
}

/**
 * Compress multiple files. Non-image files pass through unchanged.
 *
 * @param {File[]} files
 * @returns {Promise<File[]>}
 */
export async function compressImageFiles(files) {
  return Promise.all(files.map(compressImageFile));
}
